import React, { useRef, useState, useEffect, useCallback } from 'react';
import { CollapsibleFilterContainerTW } from './CollapsibleFilterContainerTW';
import { FilterChipsContainerTW } from './FilterChipsContainerTW';
import { DateRangeFilterTW } from './filters/DateRangeFilterTW';
import { TextInputFilterTW } from './filters/TextInputFilterTW';
import { MultiCheckFilterTW } from './filters/MultiCheckFilterTW';
import { SelectFilterTW } from './filters/SelectFilterTW';
import { FilterIcon, CloseIcon } from './Icons';
import './filterComponents.css';

// Placeholder for filter components that will be implemented later
const FILTER_COMPONENTS = {
    dateRange: DateRangeFilterTW,
    multiCheck: MultiCheckFilterTW,
    textInput: TextInputFilterTW,
    select: SelectFilterTW,
    searchableSelect: SelectFilterTW, // Using the same component for now
};

// Helper function to convert filterValues to activeFilters array
const getActiveFilters = (filterValues) => {
    return Object.entries(filterValues)
        .filter(([_, value]) => value !== null)
        .flatMap(([_, value]) => {
            if (Array.isArray(value)) {
                return value;
            }
            return [value];
        });
};

const FilterMenuControlTW = ({ filterConfigs = [], onFilterChange, className, initialFilters = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [committedFilters, setCommittedFilters] = useState({});
    const [draftFilters, setDraftFilters] = useState({});
    const [expandedFilterKey, setExpandedFilterKey] = useState(null);
    const buttonRef = useRef(null);
    const initialFilterProcessed = useRef(false);

    const handleToggle = () => {
        if (!isOpen) {
            // When opening, initialize draft with current committed filters
            setDraftFilters({ ...committedFilters });
        }
        setIsOpen(!isOpen);
    };

    const updateDraftFilter = (filterKey, filterData) => {
        setDraftFilters((prev) => ({
            ...prev,
            [filterKey]: filterData,
        }));
    };

    // Parse initialFilters and apply filters
    const parseInitialFilters = useCallback(() => {
        if (!initialFilters || !Array.isArray(initialFilters) || initialFilterProcessed.current) {
            return;
        }

        // Convert the flat array format to the grouped format needed internally
        const parsedFilters = {};

        initialFilters.forEach(filter => {
            const { filterKey, data, type } = filter;
            
            if (!filterKey) return;
            
            // Handle different filter types appropriately
            if (filterKey === 'dateRange' && data && data.startDate && data.endDate) {
                // For date range, we use the filter object directly
                parsedFilters[filterKey] = filter;
            } 
            else if (type === 'multiCheck') {
                // For multiCheck, we need to group values by filterKey
                if (!parsedFilters[filterKey]) {
                    parsedFilters[filterKey] = [];
                }
                
                // Add to the array of values for this filter key
                parsedFilters[filterKey].push(filter);
            }
            else {
                // For text input and other types, store in an array to handle multiple values
                if (!parsedFilters[filterKey]) {
                    parsedFilters[filterKey] = [];
                }
                parsedFilters[filterKey].push(filter);
            }
        });

        // Update both draft and committed filters
        setDraftFilters(parsedFilters);
        setCommittedFilters(parsedFilters);

        // Notify about filter changes
        if (onFilterChange && Object.keys(parsedFilters).length > 0) {
            const activeFilters = getActiveFilters(parsedFilters);
            onFilterChange(activeFilters);
        }

        // Mark initial filter as processed to avoid reprocessing
        initialFilterProcessed.current = true;
    }, [initialFilters, onFilterChange]); // getActiveFilters is defined outside component and doesn't change

    // Process initial filters on component mount
    useEffect(() => {
        parseInitialFilters();
    }, [parseInitialFilters]);

    const commitFilters = () => {
        if (onFilterChange) {
            setCommittedFilters(draftFilters);
            const activeFilters = getActiveFilters(draftFilters);
            onFilterChange(activeFilters);
            setIsOpen(false);
        }
    };

    const deleteAndCommitFilter = (filtersToDelete) => {
        const filtersArray = Array.isArray(filtersToDelete) ? filtersToDelete : [filtersToDelete];
        const newCommittedFilters = { ...committedFilters };

        filtersArray.forEach((filterToDelete) => {
            const currentValue = newCommittedFilters[filterToDelete.filterKey];

            // If it's an array of filters (like MultiCheckFilter), only remove the specific item
            if (Array.isArray(currentValue)) {
                newCommittedFilters[filterToDelete.filterKey] = currentValue.filter(
                    (item) => item.chipLabel !== filterToDelete.chipLabel
                );
                // If no items left, remove the key
                if (newCommittedFilters[filterToDelete.filterKey].length === 0) {
                    delete newCommittedFilters[filterToDelete.filterKey];
                }
            } else {
                // For single-value filters, remove the whole key
                delete newCommittedFilters[filterToDelete.filterKey];
            }
        });

        setCommittedFilters(newCommittedFilters);
        setDraftFilters(newCommittedFilters); // Keep draft in sync when deleting
        onFilterChange(getActiveFilters(newCommittedFilters));
    };

    const handleChipClick = (filter) => {
        // Open the filter menu if it's not already open
        if (!isOpen) {
            setIsOpen(true);
        }
        
        // Set the expanded filter key
        setExpandedFilterKey(filter.filterKey);
    };

    const renderFilterComponent = (config) => {
        const FilterComponent = config.component || FILTER_COMPONENTS[config.type];
        if (!FilterComponent) {
            console.warn(`No component found for filter type: ${config.type}`);
            return null;
        }

        // Get current value from draft filters when menu is open
        const currentValue = draftFilters[config.filterKey];

        return (
            <FilterComponent
                type={config.type}
                label={config.label}
                filterKey={config.filterKey}
                deletable={config.deletable}
                onFilterChange={(data) => updateDraftFilter(config.filterKey, data)}
                currentValue={currentValue}
                {...(config.props || {})}
            />
        );
    };

    // Use committed filters for the chips display
    const activeFilters = getActiveFilters(committedFilters);

    // Add a useEffect to reset expandedFilterKey when the menu closes
    useEffect(() => {
        if (!isOpen) {
            setExpandedFilterKey(null);
        }
    }, [isOpen]);

    return (
        <div className="flex flex-col gap-2 filter-components">
            <div className="flex items-center gap-2 relative">
                <button
                    ref={buttonRef}
                    className={`filter-button ${className || ''}`}
                    onClick={handleToggle}
                    aria-label="Filter options"
                >
                    <FilterIcon />
                </button>

                <div className="flex-1">
                    <FilterChipsContainerTW
                        activeFilters={activeFilters}
                        filterConfigs={filterConfigs}
                        onDelete={deleteAndCommitFilter}
                        onChipClick={handleChipClick}
                    />
                </div>

                {isOpen && (
                    <div className="absolute top-full left-0 mt-2 z-50">
                        <div
                            className="min-w-[400px] max-w-[600px] w-auto rounded-lg shadow-xl flex flex-col max-h-[80vh] transform transition-transform duration-200 ease-out filter-menu-bg"
                            style={{ overflow: 'hidden' }}
                        >
                            <div className="flex justify-between items-center p-4 border-b border-gray-200 filter-menu-bg">
                                <h2 className="text-lg font-medium">Filter Options</h2>
                                <button className="close-button" onClick={handleToggle} aria-label="close filter menu">
                                    <CloseIcon />
                                </button>
                            </div>

                            <div className="px-8 py-6 max-h-[calc(80vh-130px)] overflow-y-auto filter-menu-bg">
                                {filterConfigs.map((config) => (
                                    <CollapsibleFilterContainerTW
                                        key={config.filterKey}
                                        filterName={config.label}
                                        className="mb-4"
                                        isExpanded={config.filterKey === expandedFilterKey}
                                        onToggle={(isExpanded) => {
                                            // If the filter is being closed, clear the expandedFilterKey
                                            if (!isExpanded && config.filterKey === expandedFilterKey) {
                                                setExpandedFilterKey(null);
                                            }
                                            // If the filter is being expanded, set it as the expandedFilterKey
                                            else if (isExpanded) {
                                                setExpandedFilterKey(config.filterKey);
                                            }
                                        }}
                                    >
                                        {renderFilterComponent(config)}
                                    </CollapsibleFilterContainerTW>
                                ))}
                            </div>

                            <div className="flex justify-end p-4 border-t border-gray-200 filter-menu-bg">
                                <button className="apply-button" onClick={commitFilters}>
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export { FilterMenuControlTW };
